import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.jsx'

// Sentry — error tracking + performance monitoring (HORUS frontend).
// DSN dari env (VITE_SENTRY_DSN). Kalau missing, init di-skip secara graceful
// (Sentry SDK no-op kalau dsn undefined). Production deploy Andra set env di
// Vercel dashboard. Local dev: .env.local.
//
// Project: quantum-ops-2026/horus-frontend (Sentry EU region).
// Created: 2026-05-18 post-demo cleanup hardening.
//
// Scope minimal hari ini (post-demo cleanup):
//   ✓ Error capture client-side (render errors, unhandled promise, fetch fail)
//   ✓ Breadcrumb (navigation, fetch, click) — default integrations
//   ✓ Release tag = git commit SHA via Vercel env VERCEL_GIT_COMMIT_SHA
//   ✗ Source maps upload (defer — butuh Sentry CLI + auth token build-time)
//   ✗ Performance tracing full (defer — tracesSampleRate 0 untuk minimal cost)
//   ✗ Session replay (privacy concern untuk demo pemerintah — defer)
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' | 'production'
    // Release tag — Vercel inject VERCEL_GIT_COMMIT_SHA otomatis di build.
    // Fallback ke 'dev-local' untuk dev environment.
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || 'dev-local',
    // Performance monitoring — 10% sampling untuk Apify+HF call insight.
    // Cost bounded (10% of requests traced), bisa di-tune di production.
    tracesSampleRate: 0.1,
    // Session replay: record HANYA saat error (privacy-safe — no full session storage).
    // 0% baseline session, 100% pada error → operator action context untuk debug.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Replay integrations dgn privacy mask
    integrations: [
      Sentry.replayIntegration({
        // Mask all text content by default (privacy untuk demo pemerintah).
        // Operator yang debug bisa lihat structure DOM tanpa actual content.
        maskAllText: true,
        // Block sensitive form input (NIK, password, dst).
        maskAllInputs: true,
        // Block media element (foto KTP, candidate photos) — privacy.
        blockAllMedia: true,
      }),
      Sentry.browserTracingIntegration(),
    ],
    // Filter sensitive data sebelum kirim ke Sentry.
    beforeSend(event) {
      // Strip HF_TOKEN, SUPABASE_SERVICE_KEY, dll dari error stack/breadcrumb
      // kalau accidentally leak ke client.
      const stripSecrets = (str) => {
        if (typeof str !== 'string') return str;
        return str
          .replace(/hf_[A-Za-z0-9]{32,}/g, 'hf_<REDACTED>')
          .replace(/sntrys_[A-Za-z0-9]{32,}/g, 'sntrys_<REDACTED>')
          .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, 'jwt_<REDACTED>');
      };
      if (event.message) event.message = stripSecrets(event.message);
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(bc => ({
          ...bc,
          message: stripSecrets(bc.message),
          data: bc.data ? Object.fromEntries(
            Object.entries(bc.data).map(([k, v]) => [k, stripSecrets(v)])
          ) : bc.data,
        }));
      }
      return event;
    },
  });
  console.log('[Sentry] Initialized — env:', import.meta.env.MODE);
} else {
  console.log('[Sentry] DSN not set — error tracking disabled');
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
      <div style={{
        padding: 24, minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', background: '#02060e',
        color: '#fff', fontFamily: '"IBM Plex Mono", monospace',
      }}>
        <h1 style={{ fontSize: 18, color: '#ff2d55', marginBottom: 12 }}>⚠ Application Error</h1>
        <p style={{ fontSize: 12, color: '#999', marginBottom: 16, textAlign: 'center', maxWidth: 480 }}>
          HORUS encountered an unexpected error. Error details have been sent to monitoring.
        </p>
        <pre style={{ fontSize: 10, color: '#666', maxWidth: '90vw', overflow: 'auto', marginBottom: 20 }}>
          {error?.message || 'Unknown error'}
        </pre>
        <button onClick={resetError} style={{
          padding: '8px 20px', background: 'rgba(0,212,255,0.15)', color: '#00d4ff',
          border: '1px solid #00d4ff', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
        }}>Reload Component</button>
        <p style={{ fontSize: 9, color: '#444', marginTop: 16 }}>
          © 2026 KES (PT Kartika Eka Satria)
        </p>
      </div>
    )}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
